/**
 * Site header
 *
 * - Mobile menu: the burger opens the full-screen menu. While it is open the
 *   rest of the page is made inert, so keyboard and screen reader users stay
 *   in the header, and the page behind cannot scroll. Escape closes it and
 *   returns focus to the burger. Opening it from the keyboard moves focus to
 *   the first menu item.
 * - Header background once the page has scrolled.
 * - Search overlay: opens below the header, focuses the input, and closes on
 *   Escape (focus back to the search button) or a click elsewhere.
 *
 * The dropdowns and the mobile drill-down live in assets/js/mega-menu.js; the
 * two talk through the 'tdwd:menu-open', 'tdwd:search-open' and
 * 'tdwd:nav-close' document events.
 */
(function () {
    'use strict';

    var DESKTOP_QUERY = '(min-width: 1248px)';

    function init() {
        var header = document.querySelector('[data-site-header]');

        if (!header) {
            return;
        }

        var desktop = window.matchMedia(DESKTOP_QUERY);
        var navToggle = header.querySelector('.nav-toggle');
        var navMain = header.querySelector('[data-mega-nav]');
        var searchBtn = header.querySelector('.nav-search-btn');
        var searchOverlay = document.getElementById('site-search-overlay');
        var searchInput = searchOverlay ? searchOverlay.querySelector('.search-overlay__input') : null;

        // ------------------------------------------------------------------
        // Mobile menu
        // ------------------------------------------------------------------

        function isNavOpen() {
            return navToggle && navToggle.getAttribute('aria-expanded') === 'true';
        }

        // Everything outside the header, made inert while the menu covers it.
        function setPageInert(inert) {
            Array.prototype.forEach.call(document.body.children, function (node) {
                if (node === header || node.tagName === 'SCRIPT') {
                    return;
                }

                if (inert) {
                    node.setAttribute('inert', '');
                } else {
                    node.removeAttribute('inert');
                }
            });
        }

        function setNav(open, options) {
            if (!navToggle || !navMain) {
                return;
            }

            navToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
            navMain.classList.toggle('is-active', open);
            header.classList.toggle('nav-open', open);
            document.documentElement.classList.toggle('has-nav-open', open);
            setPageInert(open);

            if (!open) {
                closeSearch();
                document.dispatchEvent(new CustomEvent('tdwd:nav-close'));
            }

            if (open && options && options.focusFirst) {
                var first = navMain.querySelector('a[href], button:not([disabled])');

                if (first) {
                    first.focus();
                }
            }

            if (!open && options && options.returnFocus) {
                navToggle.focus();
            }
        }

        if (navToggle && navMain) {
            navToggle.addEventListener('click', function (event) {
                // event.detail is 0 when the click came from Enter or Space.
                setNav(!isNavOpen(), { focusFirst: event.detail === 0 });
            });

            document.addEventListener('keydown', function (event) {
                if (event.key === 'Escape' && isNavOpen() && (!searchOverlay || searchOverlay.hidden)) {
                    setNav(false, { returnFocus: true });
                }
            });

            var onBreakpoint = function () {
                if (desktop.matches && isNavOpen()) {
                    setNav(false);
                }
            };

            if (typeof desktop.addEventListener === 'function') {
                desktop.addEventListener('change', onBreakpoint);
            } else if (typeof desktop.addListener === 'function') {
                desktop.addListener(onBreakpoint);
            }
        }

        // ------------------------------------------------------------------
        // Header background on scroll
        // ------------------------------------------------------------------

        var SCROLL_OFFSET = 20;
        var ticking = false;

        function setScrolledState() {
            header.classList.toggle('is-scrolled', window.scrollY > SCROLL_OFFSET);
            ticking = false;
        }

        window.addEventListener('scroll', function () {
            if (!ticking) {
                ticking = true;
                window.requestAnimationFrame(setScrolledState);
            }
        }, { passive: true });

        setScrolledState();

        // ------------------------------------------------------------------
        // Search overlay
        // ------------------------------------------------------------------

        function openSearch() {
            if (!searchOverlay || !searchBtn) {
                return;
            }

            searchOverlay.hidden = false;
            searchBtn.setAttribute('aria-expanded', 'true');
            header.classList.add('search-open');
            document.dispatchEvent(new CustomEvent('tdwd:search-open'));

            if (searchInput) {
                searchInput.focus();
            }
        }

        function closeSearch() {
            if (!searchOverlay || !searchBtn || searchOverlay.hidden) {
                return;
            }

            searchOverlay.hidden = true;
            searchBtn.setAttribute('aria-expanded', 'false');
            header.classList.remove('search-open');
        }

        if (searchBtn && searchOverlay) {
            searchBtn.addEventListener('click', function () {
                if (searchOverlay.hidden) {
                    openSearch();
                } else {
                    closeSearch();
                }
            });

            searchOverlay.addEventListener('keydown', function (event) {
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    closeSearch();
                    searchBtn.focus();
                }
            });

            document.addEventListener('click', function (event) {
                if (searchOverlay.hidden) {
                    return;
                }

                if (!searchOverlay.contains(event.target) && !searchBtn.contains(event.target)) {
                    closeSearch();
                }
            });

            document.addEventListener('tdwd:menu-open', closeSearch);
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
